import { Route } from '@angular/router';
import { initialDataResolver } from 'app/app.resolvers';
import { AuthGuard } from 'app/core/auth/guards/auth.guard';
import { NoAuthGuard } from 'app/core/auth/guards/noAuth.guard';
import { LayoutComponent } from 'app/layout/layout.component';
import { NotFoundComponent } from './modules/not-found/not-found.component';

// @formatter:off
/* eslint-disable max-len */
/* eslint-disable @typescript-eslint/explicit-function-return-type */
export const appRoutes: Route[] = [

    // Redirect empty path to '/example'
    {path: '', pathMatch : 'full', redirectTo: 'home'},


    // Redirect signed-in user to the '/example'
    //
    // After the user signs in, the sign-in page will redirect the user to the 'signed-in-redirect'
    // path. Below is another redirection for that path to redirect the user to the desired
    // location. This is a small convenience to keep all main routes together here on this file.
    {path: 'signed-in-redirect', pathMatch : 'full', redirectTo: 'home'},

    // Auth routes for guests
    {
        path: '',
        canActivate: [NoAuthGuard],
        canActivateChild: [NoAuthGuard],
        component: LayoutComponent,
    
        resolve: {
            initialData: initialDataResolver
        },
        children: [
            {path: 'verify', loadComponent: () => import('app/modules/auth/confirmation-required/confirmation-required.component').then(comp => comp.AuthConfirmationRequiredComponent),},
            {path: 'forgot-password', loadComponent: () => import('app/modules/auth/forgot-password/forgot-password.component').then(comp => comp.AuthForgotPasswordComponent),
            data: {
                title: 'Supersonic - Reset your Password',
                description: 'Reset your Supersonic account password.',
                keywords: 'Supersonic Forget Password',

                ogTitle: 'Supersonic - Reset your Password',
                ogDescription: 'Reset your Supersonic account password.',
                ogImage: 'https://supersonic.co.za/assets/images/logo/logo.svg',
                ogUrl: 'https://supersonic.co.za/forgot-password',
                ogType: 'website',

                twCard: 'summary_large_image',
                twTitle: 'Supersonic - Reset your Password.',
                twDescription: 'Reset your Supersonic account password.',
                twImage: 'https://supersonic.co.za/assets/images/logo/logo.svg',
                canonical: 'https://supersonic.co.za/forgot-password'
            }
            },
            {path: 'reset', loadComponent: () => import('app/modules/auth/reset-password/reset-password.component').then(comp => comp.AuthResetPasswordComponent)},
            {path: 'sign-in', loadComponent: () => import('app/modules/auth/sign-in/sign-in.component').then(comp => comp.AuthSignInComponent),
            data: {
                title: 'Supersonic Login | Sign In to Your Uncapped Fibre, 5G & LTE Account',
                description: 'Log in to your Supersonic account to manage your uncapped fibre, 5G, LTE or AirFibre speeds, view invoices, upgrade packages, run speed tests and control your services 24/7. South Africa’s fastest-growing ISP.',
                keywords: 'supersonic login, supersonic sign in, supersonic account, supersonic fibre login, supersonic 5g login, supersonic customer portal, supersonic uncapped internet login, supersonic south africa',
                ogTitle: 'Supersonic Login – Manage Your Uncapped Fibre & 5G Account',
                ogDescription: 'Sign in to your Supersonic account and take control of your blazing-fast uncapped fibre, 5G or LTE connection. Check speeds, pay bills, upgrade packages instantly.',
                ogImage: 'https://supersonic.co.za/assets/images/logo/logo.svg',
                ogUrl: 'https://supersonic.co.za/sign-in',
                ogType: 'website',
                twCard: 'summary_large_image',
                twTitle: 'Supersonic Login – Manage Your Uncapped Fibre & 5G Account',
                twDescription: 'Sign in to your Supersonic account and take control of your blazing-fast uncapped fibre, 5G or LTE connection. Check speeds, pay bills, upgrade instantly.',
                twImage: 'https://supersonic.co.za/assets/images/logo/logo.svg',
                canonical: 'https://supersonic.co.za/sign-in',
                robots: 'noindex, follow',
                hreflang: [
                    { lang: 'en-za', url: 'https://supersonic.co.za/sign-in' },
                    { lang: 'x-default', url: 'https://supersonic.co.za/sign-in' }
                ]
            }
            },
            {path: 'sign-up', loadComponent: () => import('app/modules/auth/sign-up/sign-up.component').then(comp => comp.AuthSignUpComponent),
            data: {
                title: 'Supersonic Sign Up | Get Uncapped Fibre, 5G or LTE in Minutes',
                description: 'Create your Supersonic account and get South Africa’s fastest uncapped fibre, 5G, LTE or AirFibre. Instant activation, no contracts, no throttling. Sign up online in under 3 minutes.',
                keywords: 'supersonic sign up, supersonic register, supersonic create account, supersonic fibre sign up, supersonic 5g registration, supersonic uncapped internet sign up, supersonic new customer, supersonic south africa',
                ogTitle: 'Supersonic Sign Up – Get Uncapped Fibre & 5G in Minutes',
                ogDescription: 'Join thousands of happy customers. Sign up for Supersonic today and enjoy blazing-fast uncapped fibre, 5G or LTE with no contracts and instant activation.',
                ogImage: 'https://supersonic.co.za/assets/images/logo/logo.svg',
                ogUrl: 'https://supersonic.co.za/sign-up',
                ogType: 'website',
                twCard: 'summary_large_image',
                twTitle: 'Supersonic Sign Up – Uncapped Fibre & 5G in Minutes',
                twDescription: 'Join Supersonic today! Get uncapped fibre, 5G or LTE with no contracts, instant activation and South Africa’s best speeds. Sign up online now.',
                twImage: 'https://supersonic.co.za/assets/images/logo/logo.svg',
                canonical: 'https://supersonic.co.za/sign-up',
                robots: 'index, follow',
                hreflang: [
                    { lang: 'en-za', url: 'https://supersonic.co.za/sign-up' },
                    { lang: 'x-default', url: 'https://supersonic.co.za/sign-up' }
                ]
            }
            },
            {path: 'sign-up-stepper', loadComponent: () => import('app/modules/auth/sign-up-stepper/sign-up-stepper-page.component').then(comp => comp.SignUpStepperPageComponent),
            data: {
                title: 'Supersonic - Create an Account',
                description: 'Create your Supersonic account with our step-by-step process.',
                keywords: 'Supersonic Sign Up Register Stepper',

                ogTitle: 'Supersonic - Create an Account',
                ogDescription: 'Create your Supersonic account with our step-by-step process.',
                ogImage: 'https://supersonic.co.za/assets/images/logo/logo.svg',
                ogUrl: 'https://supersonic.co.za/sign-up-stepper',
                ogType: 'website',

                twCard: 'summary_large_image',
                twTitle: 'Supersonic - Create an Account',
                twDescription: 'Create your Supersonic account with our step-by-step process.',
                twImage: 'https://supersonic.co.za/assets/images/logo/logo.svg',
                canonical: 'https://supersonic.co.za/sign-up-stepper'
            }
            },
        ]
    },

    // Auth routes for authenticated users
    {
        path: '',
        canActivate: [AuthGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: initialDataResolver
        },
        children: [
            {path: 'sign-out', loadComponent: () => import('app/modules/auth/sign-out/sign-out.component').then(comp => comp.AuthSignOutComponent)},
            //{path: 'unlock-session', loadChildren: () => import('app/modules/auth/unlock-session/')}
        ]
    },

    {
        path: '',
        component: LayoutComponent,
        resolve: {
            initialData: initialDataResolver
        },
        children: [
            {path: 'terms-and-conditions', loadComponent: () => import('app/modules/terms-and-conditions/terms-and-conditions.component').then(comp => comp.TermsAndConditionsComponent),
            data: {
                title: 'Supersonic Terms & Conditions | Fibre, 5G, LTE & AirFibre South Africa',
                description: 'Read the full Supersonic Terms and Conditions for uncapped fibre, 5G, LTE and AirFibre services. Includes fair usage, billing, cancellation policy, service guarantees and consumer rights in South Africa.',
                keywords: 'supersonic terms and conditions, supersonic t&cs, supersonic fibre terms, supersonic contract terms, supersonic cancellation policy, supersonic fair usage policy, supersonic south africa terms',
                ogTitle: 'Supersonic Terms & Conditions – Fibre, 5G & LTE Services',
                ogDescription: 'Official Supersonic Terms and Conditions for uncapped internet services in South Africa. Transparent policies on billing, cancellations, fair usage and consumer protection.',
                ogImage: 'https://supersonic.co.za/assets/images/logo/logo.svg',
                ogUrl: 'https://supersonic.co.za/terms-and-conditions',
                ogType: 'website',
                twCard: 'summary_large_image',
                twTitle: 'Supersonic Terms & Conditions – Fibre, 5G & LTE Services',
                twDescription: 'Clear and transparent Supersonic Terms & Conditions for all uncapped fibre, 5G and LTE packages across South Africa.',
                twImage: 'https://supersonic.co.za/assets/images/logo/logo.svg',
                canonical: 'https://supersonic.co.za/terms-and-conditions',
                robots: 'index, follow',
                hreflang: [
                    { lang: 'en-za', url: 'https://supersonic.co.za/terms-and-conditions' },
                    { lang: 'x-default', url: 'https://supersonic.co.za/terms-and-conditions' }
                ]
            }
            },
            {
                path: 'contact-us', loadComponent: () => import('app/modules/contact-us/contact-us.component').then(comp => comp.ContactUsComponent),
                data: {
                    title: 'Contact Supersonic | Phone, WhatsApp, Email & Live Chat Support SA',
                    description: 'Get instant help from Supersonic South Africa. Contact our local team via phone, WhatsApp, email, live chat or ticket for sales enquiries, technical support, billing, moves or fibre/5G coverage questions.',
                    keywords: 'contact supersonic, supersonic contact number, supersonic phone number, supersonic whatsapp, supersonic customer care, supersonic support, supersonic email, supersonic helpline, supersonic live chat, supersonic south africa contact',
                    ogTitle: 'Contact Supersonic – Phone, WhatsApp & Support (South Africa)',
                    ogDescription: 'Need help? Contact Supersonic instantly via phone, WhatsApp, email or live chat. South African support team ready for sales, billing, technical & coverage queries.',
                    ogImage: 'https://supersonic.co.za/assets/images/logo/logo.svg',
                    ogUrl: 'https://supersonic.co.za/contact-us',
                    ogType: 'website',
                    twCard: 'summary_large_image',
                    twTitle: 'Contact Supersonic – Phone, WhatsApp & Support (South Africa)',
                    twDescription: 'Contact Supersonic South Africa instantly – phone, WhatsApp, email or live chat. Fast local support for fibre, 5G, LTE & AirFibre customers.',
                    twImage: 'https://supersonic.co.za/assets/images/logo/logo.svg',
                    canonical: 'https://supersonic.co.za/contact-us',
                    robots: 'index, follow',
                    hreflang: [
                        { lang: 'en-za', url: 'https://supersonic.co.za/contact-us' },
                        { lang: 'x-default', url: 'https://supersonic.co.za/contact-us' }
                    ]
                }
            },
            {                
                path: 'contact-us/complaints', loadComponent: () => import('app/modules/contact-us/complaints/complaints.component').then(comp => comp.ComplaintsComponent),
                data: {
                    title: 'Supersonic Complaints Procedure | Submit a Formal Complaint (South Africa)',
                    description: 'Unresolved Supersonic fibre, 5G or LTE issue? Follow our official ICASA-compliant Complaints Handling Procedure. Submit a formal complaint easily online and get fast, fair resolution from our South African escalations team.',
                    keywords: 'supersonic complaints procedure, supersonic formal complaint, supersonic icasa complaint, supersonic unresolved issue, supersonic escalation process, supersonic customer complaints south africa, supersonic fibre complaint, supersonic support escalation, submit complaint supersonic',
                    ogTitle: 'Supersonic Complaints Procedure – Fast & Fair Resolution (SA)',
                    ogDescription: 'Still experiencing issues after ClientZone? Use Supersonic’s official, ICASA-approved complaints process. Submit your formal complaint online and get priority resolution from our South African team.',
                    ogImage: 'https://supersonic.co.za/assets/images/logo/logo.svg',
                    ogUrl: 'https://supersonic.co.za/contact-us/complaints',
                    ogType: 'website',
                    twCard: 'summary_large_image',
                    twTitle: 'Supersonic Complaints Procedure – Fast & Fair Resolution (SA)',
                    twDescription: 'Official Supersonic complaints process. Submit unresolved fibre, 5G or LTE issues here for priority escalation and ICASA-compliant resolution in South Africa.',
                    twImage: 'https://supersonic.co.za/assets/images/logo/logo.svg',
                    canonical: 'https://supersonic.co.za/contact-us/complaints',
                    robots: 'index, follow',
                    hreflang: [
                        { lang: 'en-za', url: 'https://supersonic.co.za/contact-us/complaints' },
                        { lang: 'x-default', url: 'https://supersonic.co.za/contact-us/complaints' }
                    ]
                }
            },
            {
                path: 'about-us', loadComponent: () => import('app/modules/about-us/about-us.component').then(comp => comp.AboutUsComponent),
                data: {
                    title: 'About Supersonic | South Africa’s Fastest-Growing Uncapped Fibre & 5G ISP',
                    description: 'Discover Supersonic – the customer-first South African ISP delivering blazing-fast uncapped fibre, 5G, LTE and AirFibre with no throttling, no contracts and award-winning support. Learn about our mission, values and why thousands choose us every month.',
                    keywords: 'about supersonic, supersonic south africa, supersonic isp, supersonic fibre, supersonic 5g, supersonic company, supersonic mission and values, supersonic team, uncapped internet south africa, best isp south africa, supersonic mtn',
                    ogTitle: 'About Supersonic – SA’s Fastest-Growing Uncapped Fibre & 5G ISP',
                    ogDescription: 'Meet Supersonic: South Africa’s customer-obsessed ISP bringing truly uncapped fibre, 5G and AirFibre to homes and businesses. No throttling, no nonsense – just fast, reliable internet.',
                    ogImage: 'https://supersonic.co.za/assets/images/logo/logo.svg',
                    ogUrl: 'https://supersonic.co.za/about-us',
                    ogType: 'website',
                    twCard: 'summary_large_image',
                    twTitle: 'About Supersonic – SA’s Fastest-Growing Uncapped Fibre & 5G ISP',
                    twDescription: 'South Africa’s favourite new ISP. Uncapped fibre, 5G & AirFibre with no contracts and no throttling. Discover why Supersonic is growing faster than anyone else.',
                    twImage: 'https://supersonic.co.za/assets/images/logo/logo.svg',
                    canonical: 'https://supersonic.co.za/about-us',
                    robots: 'index, follow',
                    hreflang: [
                        { lang: 'en-za', url: 'https://supersonic.co.za/about-us' },
                        { lang: 'x-default', url: 'https://supersonic.co.za/about-us' }
                    ]
                }
            },
            // {path: 'faq', loadComponent: () => import('app/modules/faq/faq.component').then(comp => comp.FaqComponent)},
            {
                path: 'product-overview', loadChildren: () => import('app/modules/catalog/catalog.routes'),
                data: {
                    title: 'Supersonic Products | Uncapped Fibre, 5G & AirFibre Packages South Africa',
                    description: 'Discover Supersonic’s full range of blazing-fast internet products: truly uncapped fibre, 5G home internet and AirFibre. No throttling, no contracts – find the perfect high-speed package for gaming, streaming, work or home use.',
                    keywords: 'supersonic products, supersonic packages, supersonic fibre packages, supersonic 5g packages, supersonic airfibre, uncapped internet south africa, best fibre deals sa, supersonic internet options',
                    ogTitle: 'Supersonic Products – Uncapped Fibre, 5G & AirFibre (South Africa)',
                    ogDescription: 'Explore all Supersonic internet packages: uncapped fibre, 5G and AirFibre. No contracts, no fair usage policy, just pure speed. Find your perfect plan today.',
                    ogImage: 'https://supersonic.co.za/assets/images/logo/logo.svg',
                    ogUrl: 'https://supersonic.co.za/product-overview',
                    ogType: 'website',
                    twCard: 'summary_large_image',
                    twTitle: 'Supersonic Products – Uncapped Fibre, 5G & AirFibre Deals',
                    twDescription: 'All Supersonic internet packages in one place: uncapped fibre, 5G & AirFibre. No throttling, no contracts, instant activation across South Africa.',
                    twImage: 'https://supersonic.co.za/assets/images/logo/logo.svg',
                    canonical: 'https://supersonic.co.za/product-overview',
                    robots: 'index, follow',
                    hreflang: [
                        { lang: 'en-za', url: 'https://supersonic.co.za/product-overview' },
                        { lang: 'x-default', url: 'https://supersonic.co.za/product-overview' }
                    ]
                }
            },
            {path: 'products', loadChildren: () => import('app/modules/products/products.routes')},
            {
                path: 'support', loadComponent: () => import('app/modules/support/support.component').then(comp => comp.SupportComponent),
                data: {
                    title: 'Supersonic Support | Help Centre, FAQs & Troubleshooting South Africa',
                    description: 'Get instant help with your Supersonic fibre, 5G or AirFibre connection. Browse FAQs, speed test guides, router setup, outage checker and contact our South African support team via phone, WhatsApp, email or live chat.',
                    keywords: 'supersonic support, supersonic help centre, supersonic faqs, supersonic troubleshooting, supersonic fibre support, supersonic 5g support, supersonic airfibre help, supersonic customer service, supersonic outage checker, supersonic south africa support',
                    ogTitle: 'Supersonic Support – Help Centre & FAQs (South Africa)',
                    ogDescription: 'Fast answers for Supersonic customers. Fix fibre, 5G or AirFibre issues, check coverage, run speed tests or contact our local support team 7 days a week.',
                    ogImage: 'https://supersonic.co.za/assets/images/logo/logo.svg',
                    ogUrl: 'https://supersonic.co.za/support',
                    ogType: 'website',
                    twCard: 'summary_large_image',
                    twTitle: 'Supersonic Support – Help Centre & FAQs (South Africa)',
                    twDescription: 'Supersonic Help Centre: FAQs, troubleshooting guides, speed tests and direct contact with our South African support team – available 7 days a week.',
                    twImage: 'https://supersonic.co.za/assets/images/logo/logo.svg',
                    canonical: 'https://supersonic.co.za/support',
                    robots: 'index, follow',
                    hreflang: [
                        { lang: 'en-za', url: 'https://supersonic.co.za/support' },
                        { lang: 'x-default', url: 'https://supersonic.co.za/support' }
                    ]
                }
            },
            //{path: 'network', loadChildren: () => import('app/modules/network-status/network-status.routes')},
            {path: 'blogs', loadChildren: () => import('app/modules/blogs/blogs.routes'),
            data: {
                title: 'Supersonic Blog | Fibre Tips, 5G News, Gaming & Internet Guides SA',
                description: 'Latest fibre & 5G news, speed test guides, router setup tips, gaming on uncapped internet, work-from-home hacks and everything you need to get the most out of your Supersonic connection in South Africa.',
                keywords: 'supersonic blog, fibre internet tips, 5g south africa news, uncapped internet guides, gaming on fibre sa, router setup supersonic, speed test south africa, work from home internet tips, supersonic updates',
                ogTitle: 'Supersonic Blog – Fibre, 5G, Gaming & Internet Tips (South Africa)',
                ogDescription: 'Stay updated with the Supersonic Blog: latest fibre rollouts, 5G speed records, gaming router guides, streaming tips and exclusive offers for South African customers.',
                ogImage: 'https://supersonic.co.za/assets/images/logo/logo.svg',
                ogUrl: 'https://supersonic.co.za/blogs',
                ogType: 'website',
                twCard: 'summary_large_image',
                twTitle: 'Supersonic Blog – Fibre, 5G & Gaming Tips South Africa',
                twDescription: 'Supersonic Blog: Latest fibre & 5G news, gaming guides, router tips and exclusive deals for South African customers.',
                twImage: 'https://supersonic.co.za/assets/images/logo/logo.svg',
                canonical: 'https://supersonic.co.za/blogs',
                robots: 'index, follow',
                hreflang: [
                    { lang: 'en-za', url: 'https://supersonic.co.za/blogs' },
                    { lang: 'x-default', url: 'https://supersonic.co.za/blogs' }
                ]
            }
            },
            {path: 'pre-orders', loadComponent: () => import('app/modules/sales-agent/sales-agent.component').then(comp => comp.SalesAgentComponent),
                data: {
                    title: 'Supersonic - Pre-Order',
                }
            },
            {
                path: 'home', loadComponent: () => import('app/modules/landing/home/home.component').then(comp => comp.LandingHomeComponent),
                data: {
                    title: 'Supersonic | Uncapped Fibre, 5G & AirFibre Internet South Africa',
                    description: 'Get truly uncapped, lightning-fast internet with Supersonic. Fibre, 5G and AirFibre packages with no contracts, no throttling and instant activation across South Africa – perfect for gaming, streaming, work and home.',
                    keywords: 'supersonic, supersonic fibre, supersonic 5g, supersonic airfibre, uncapped internet south africa, uncapped fibre deals, best isp south africa, fibre coverage check, 5g home internet, no contract internet sa',
                    ogTitle: 'Supersonic – Uncapped Fibre, 5G & AirFibre Internet South Africa',
                    ogDescription: 'South Africa’s fastest-growing ISP. Truly uncapped fibre, 5G & AirFibre with no contracts, no fair-usage policy and award-winning local support. Check coverage and sign up today.',
                    ogImage: 'https://supersonic.co.za/assets/images/logo/logo.svg',
                    ogUrl: 'https://supersonic.co.za/',
                    ogType: 'website',
                    twCard: 'summary_large_image',
                    twTitle: 'Supersonic – Uncapped Fibre, 5G & AirFibre South Africa',
                    twDescription: 'Get blazing-fast, truly uncapped internet. Fibre, 5G & AirFibre packages with no contracts and instant activation across SA. Check if you’re covered now.',
                    twImage: 'https://supersonic.co.za/assets/images/logo/logo.svg',
                    canonical: 'https://supersonic.co.za/',
                    robots: 'index, follow',
                    hreflang: [
                        { lang: 'en-za', url: 'https://supersonic.co.za/' },
                        { lang: 'x-default', url: 'https://supersonic.co.za/' }
                    ]
                }
            },
            {path: 'packages', loadComponent: () => import('app/modules/order-process/packages/packages.component').then(comp => comp.PackagesComponent),
                data: {
                    title: 'Supersonic - Packages',
                }
            },
            {path: 'cart', loadComponent: () => import('app/modules/order-process/cart/cart.component').then(comp => comp.CartComponent),
                data: {
                    title: 'Supersonic - Your Cart',
                }
            },
            {path: 'order-complete', loadComponent: () => import('app/modules/order-process/order-complete/order-complete.component').then(comp => comp.OrderCompleteComponent),
                data: {
                    title: 'Supersonic - Order Complete',
                }
            },
            {path: 'debit-order-setup', loadComponent: () => import('app/modules/order-process/debit-order-setup/debit-order-setup.component').then(comp => comp.DebitOrderSetupComponent),
                data: {
                    title: 'Supersonic - Debit Order Setup',
                }
            },
            {path: 'confirm-order', loadComponent: () => import('app/modules/order-process/updated-order-journey/updated-order-journey.component').then(comp => comp.UpdatedOrderJourney),
                data: {
                    title: 'Supersonic - Confirm Your Order',
                    description: 'Review and confirm your Supersonic order details, payment information, and complete your purchase.',
                    keywords: 'Supersonic Order Confirmation, Checkout, Order Review, Complete Purchase',

                    ogTitle: 'Supersonic - Confirm Your Order',
                    ogDescription: 'Review and confirm your Supersonic order details, payment information, and complete your purchase.',
                    ogImage: 'https://supersonic.co.za/assets/images/logo/logo.svg',
                    ogUrl: 'https://supersonic.co.za/confirm-order',
                    ogType: 'website',

                    twCard: 'summary_large_image',
                    twTitle: 'Supersonic - Confirm Your Order',
                    twDescription: 'Review and confirm your Supersonic order details, payment information, and complete your purchase.'
                }
            },
            {
                path: 'deals', loadComponent: () => import('app/modules/deals/deals.component').then(comp => comp.DealsComponent),
                data: {
                    title: 'Supersonic Internet Deals - Best Discounts & Promotions in South Africa',
                    description: 'Discover the latest deals and promotions from Supersonic’s. Get the best discounts on high-speed internet services across South Africa with our exclusive offers.',
                    keywords: 'internet deals, ISP promotions, South Africa broadband discounts, Supersonic offers, internet plan deals',

                    ogTitle: 'Supersonic Internet Deals - Best Discounts & Promotions in South Africa',
                    ogDescription: 'Discover the latest deals and promotions from Supersonic’s. Get the best discounts on high-speed internet services across South Africa with our exclusive offers.',
                    ogImage: 'https://supersonic.co.za/assets/images/logo/logo.svg',
                    ogUrl: 'https://supersonic.co.za/deals',
                    ogType: 'website',

                    twCard: 'summary_large_image',
                    twTitle: 'Supersonic Internet Deals - Best Discounts & Promotions in South Africa',
                    twDescription: 'Discover the latest deals and promotions from Supersonic’s. Get the best discounts on high-speed internet services across South Africa with our exclusive offers.',
                    twImage: 'https://supersonic.co.za/assets/images/logo/logo.svg',

                    canonical: 'https://supersonic.co.za/deals'
                }
            },
            {path: 'help-centre', loadComponent: () => import('app/modules/help-section/help-section.component').then(comp => comp.HelpSectionComponent),
                data: {
                    title: 'Supersonic - Help & Support',
                }
            },
            {path: 'help-centre/topic', loadComponent: () => import('app/modules/help-section/help-section-sub/help-section-sub.component').then(comp => comp.HelpSectionSubComponent),
                data: {
                    title: 'Supersonic - Help & Support',
                }
            },
            {path: 'help-centre/topic/:slug',loadComponent: () => import('app/modules/help-section/help-section-sub/help-section-sub.component').then(comp => comp.HelpSectionSubComponent),
                data: {
                    title: 'Supersonic - Help & Support',
                }
            },
            {path: 'help-centre/topic/:slug/:slug',loadComponent: () => import('app/modules/help-section/help-section-sub/help-section-list-content/help-section-list-content.component').then(comp => comp.HelpSectionListContentComponent),
                data: {
                    title: 'Supersonic - Help & Support',
                }
            }
        ]
    },

    // Admin routes
    {
        path: '',
        canActivate: [AuthGuard],
        // canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: initialDataResolver
        },
        children: [
            // {path: 'example', loadChildren: () => import('app/modules/admin/example/example.routes')},
            {path: 'dashboard', loadComponent: () => import('app/modules/dashboard/dashboard.component').then(comp => comp.DashboardComponent),
                data: {
                    title: 'Supersonic - Dashboard',
                }
            },
            // {path: 'dashboard/services', loadComponent: () => import('app/modules/dashboard/services/services.routes')},
            // {path: 'dashboard/tickets', loadComponent: () => import('app/modules/dashboard/tickets/tickets.routes')},
            // {path: 'dashboard/payment-method', loadComponent: () => import('app/modules/shared-components/payment-method/payment-methods.routes')},
            {path: 'dashboard/profile', loadComponent: () => import('app/modules/dashboard/profile/profile.component').then(comp => comp.ProfileComponent),
                data: {
                    title: 'Supersonic - Your Profile',
                }
            },
            // {path: 'dashboard/invoiceStatements', loadComponent: () => import('app/modules/dashboard/invoice-statements/invoice-statements.routes')},
            {path: 'dashboard/requests', loadComponent: () => import('app/modules/dashboard/requests/requests.component').then(comp => comp.RequestsComponent),
                data: {
                    title: 'Supersonic - Requests',
                }
            },
            {path: 'dashboard/invoices', loadComponent: () => import('app/modules/dashboard/invoices/invoices.component').then(comp => comp.InvoicesComponent),
                data: {
                    title: 'Supersonic - Invoices',
                }
            },
            
            {path: 'balances', loadComponent: () => import('app/modules/outstanding/outstanding.component').then(comp => comp.OutstandingComponent),
                data: {
                    title: 'Supersonic - Balances',
                }
            },
            {path: 'dashboard/campaign', redirectTo: '/home', pathMatch: 'full'},
                {path: 'dashboard/package-confirmation', loadComponent: () => import('app/modules/dashboard/package-options/swiper-packages/package-confirmation-dialog/package-confirmation-dialog.component').then(comp => comp.PackageConfirmationDialogComponent),
                    data: {
                        title: 'Supersonic - Package Confirmation',
                    }
                },
            {path: 'dashboard/speed-test', loadComponent: () => import('app/modules/dashboard/speed-test/speed-test.component').then(comp => comp.SpeedTestComponent),
                data: {
                    title: 'Supersonic - Speed Test',
                }
            },
        ]
    },

   

    // 404
    {path: '**', pathMatch: 'full', component: NotFoundComponent},
]